import { useEffect, useState, useRef } from 'react'
import { Link } from 'react-router'
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement,
  LineElement, PointElement, Filler,
  Tooltip, type ChartData, type ChartOptions,
} from 'chart.js'
import { Bar, Line } from 'react-chartjs-2'

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler, Tooltip)

// ── Theme tokens ──────────────────────────────────────────────────────────────
function readTokens() {
  const s = getComputedStyle(document.documentElement)
  const g = (v: string) => s.getPropertyValue(v).trim()
  return {
    text2: g('--text-2'), text3: g('--text-3'), surface: g('--surface'),
    grid: g('--chart-grid'), critical: g('--critical'), high: g('--high'),
    medium: g('--medium'), low: g('--low'), blue: g('--blue'),
  }
}
type Tokens = ReturnType<typeof readTokens>

function useTokens(): Tokens {
  const [tokens, setTokens] = useState(readTokens)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const obs = new MutationObserver(() => setTokens(readTokens()))
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    const onChange = () => setTokens(readTokens())
    mq.addEventListener('change', onChange)
    return () => { obs.disconnect(); mq.removeEventListener('change', onChange) }
  }, [])
  return tokens
}

// ── Risk chart data ───────────────────────────────────────────────────────────
const RISK_LABELS = [
  'Geoökon. Konfrontation', 'Zwischenstaatl. Konflikt', 'Extremwetter',
  'KI-Desinformation', 'Krit. Infrastruktur', 'Technologie-Governance',
  'Lebenshaltungskosten', 'Ressourcenkrisen', 'Staatsschulden', 'Umweltkriminalität',
]
const RISK_VALUES   = [18, 15, 13, 11, 9, 8, 7, 6, 5, 4]
const RISK_SEVERITY = ['critical','critical','high','medium','high','medium','medium','medium','low','low'] as const

function risksData(t: Tokens): ChartData<'bar'> {
  const cmap = { critical: t.critical, high: t.high, medium: t.medium, low: t.low }
  return {
    labels: RISK_LABELS,
    datasets: [{ data: RISK_VALUES, backgroundColor: RISK_SEVERITY.map(s => cmap[s]),
      borderRadius: 4, borderSkipped: 'left' as const, barThickness: 17 }],
  }
}

function risksOptions(t: Tokens): ChartOptions<'bar'> {
  return {
    indexAxis: 'y', responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false },
      tooltip: { callbacks: { label: c => ` ${c.parsed.x}% der WEF-Befragten` } } },
    scales: {
      x: { grid: { color: t.grid }, border: { display: false }, max: 22,
           ticks: { color: t.text3, font: { size: 11 }, callback: v => v + '%' } },
      y: { grid: { display: false }, border: { display: false },
           ticks: { color: t.text2, font: { size: 11 } } },
    },
  }
}

function tradeData(t: Tokens): ChartData<'line'> {
  return {
    labels: ['2022', '2023', '2024', '2025', '2026*'],
    datasets: [{
      data: [3.5, 1.2, 2.8, 4.6, 1.9],
      borderColor: t.blue, borderWidth: 2.5,
      pointBackgroundColor: [t.blue, t.blue, t.blue, t.blue, t.high],
      pointRadius: [4, 4, 4, 4, 6],
      pointBorderWidth: 2, pointBorderColor: t.surface,
      fill: true, tension: 0.35,
    }],
  }
}

function tradeOptions(t: Tokens): ChartOptions<'line'> {
  return {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false },
      tooltip: { callbacks: { label: c => ` ${c.parsed.y}% Wachstum` } } },
    scales: {
      x: { grid: { display: false }, border: { display: false },
           ticks: { color: t.text3, font: { size: 11 } } },
      y: { grid: { color: t.grid }, border: { display: false },
           ticks: { color: t.text3, font: { size: 11 }, callback: v => v + '%' } },
    },
  }
}

// ── Small components ──────────────────────────────────────────────────────────
function KpiTile({ label, value, stripe }: { label: string; value: string; stripe: string }) {
  return (
    <div className="bg-surface border border-border rounded-lg px-4 py-3.5 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: `var(${stripe})` }} />
      <div className="text-[11px] font-semibold uppercase tracking-widest text-t3 mb-1.5">{label}</div>
      <div className="font-display text-[28px] font-bold tabular-nums text-t1 leading-none mb-1">{value}</div>
    </div>
  )
}

type Sev = 'critical' | 'high' | 'medium'
const SEV_BORDER: Record<Sev, string> = { critical: 'border-l-critical', high: 'border-l-high', medium: 'border-l-medium' }
const SEV_LABEL:  Record<Sev, string> = {
  critical: 'text-critical bg-[var(--critical-bg)]',
  high:     'text-high bg-[var(--high-bg)]',
  medium:   'text-medium bg-[var(--medium-bg)]',
}

function ConflictCard({ region, name, desc, sev }: { region: string; name: string; desc: string; sev: Sev }) {
  return (
    <div className={`bg-surface border border-border border-l-[3px] ${SEV_BORDER[sev]} rounded-lg p-4`}>
      <div className="text-[10px] font-bold tracking-widest uppercase text-t3 mb-1">{region}</div>
      <div className="text-[13px] font-semibold text-t1 mb-1.5 leading-snug">{name}</div>
      <p className="text-[11.5px] text-t2 leading-relaxed mb-2.5">{desc}</p>
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold tracking-wide ${SEV_LABEL[sev]}`}>
        ● {sev === 'critical' ? 'Kritisch' : sev === 'high' ? 'Hoch' : 'Erhöht'}
      </span>
    </div>
  )
}

// ── Trade gradient plugin ─────────────────────────────────────────────────────
function TradeChartWithGradient({ t }: { t: Tokens }) {
  const chartRef = useRef<ChartJS<'line'>>(null)

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    const ctx = chart.ctx
    const grad = ctx.createLinearGradient(0, 0, 0, 260)
    grad.addColorStop(0, t.blue + '44')
    grad.addColorStop(1, t.blue + '00')
    chart.data.datasets[0].backgroundColor = grad
    chart.update('none')
  }, [t])

  return <Line ref={chartRef} data={tradeData(t)} options={tradeOptions(t)} />
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Geopolitik2026() {
  const t = useTokens()

  return (
    <main className="max-w-[1160px] mx-auto px-5 py-8">
      <Link to="/analysen" className="inline-flex items-center gap-1.5 text-[12px] font-bold text-accent mb-6">
        ← Alle Analysen
      </Link>

      {/* Report header */}
      <header className="flex justify-between items-end flex-wrap gap-3 pb-5 border-b-2 border-accent mb-7">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-accent mb-1.5">Geopolitische Analyse</div>
          <h1 className="font-display text-[clamp(22px,4vw,34px)] font-bold text-t1 leading-tight">
            Weltlage &amp; Politische Trends 2026
          </h1>
        </div>
        <div className="text-right text-t3 text-[12px] leading-loose">
          <strong className="text-t2 font-medium">Stand:</strong> September 2026<br />
          <strong className="text-t2 font-medium">Quellen:</strong> WEF · ACLED · PIIE · EY · Pew Research
        </div>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-3 mb-7 max-sm:grid-cols-2">
        <KpiTile label="Aktive Konflikte"  value="56"     stripe="--critical" />
        <KpiTile label="Top-Risiko-Anteil" value="18 %"   stripe="--high" />
        <KpiTile label="US-Zollsatz 2026"  value="11.8 %" stripe="--medium" />
        <KpiTile label="Handelswachstum"   value="1.9 %"  stripe="--low" />
      </div>

      {/* Charts */}
      <h2 className="font-display text-lg font-semibold text-t1 flex items-center gap-2.5 mb-4 after:flex-1 after:h-px after:bg-border">
        Globale Risikolandschaft
      </h2>
      <div className="grid gap-3.5 mb-7" style={{ gridTemplateColumns: '1.65fr 1fr' }}>
        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="text-[13px] font-semibold text-t2 mb-0.5">Top-10-Risiken 2026</div>
          <div className="text-[11px] text-t3 mb-3.5">WEF Global Risks Report — Anteil der Befragten (%)</div>
          <div className="h-[285px]">
            <Bar data={risksData(t)} options={risksOptions(t)} />
          </div>
        </div>
        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="text-[13px] font-semibold text-t2 mb-0.5">Welthandelswachstum</div>
          <div className="text-[11px] text-t3 mb-3.5">Jährl. Veränderung globaler Warenhandel 2022–2026*</div>
          <div className="h-[285px]">
            <TradeChartWithGradient t={t} />
          </div>
        </div>
      </div>

      {/* Conflicts */}
      <h2 className="font-display text-lg font-semibold text-t1 flex items-center gap-2.5 mb-4 after:flex-1 after:h-px after:bg-border">
        Konfliktherde
      </h2>
      <div className="grid grid-cols-3 gap-3 mb-7 max-md:grid-cols-2 max-sm:grid-cols-1">
        <ConflictCard region="Naher Osten" name="Iran–USA–Israel-Konflikt" sev="critical"
          desc="Mehrfronten-Eskalation: Gaza, Libanon, Jemen (Houthis), Irak. Straße von Hormuz als globales Energiemarktrisiko." />
        <ConflictCard region="Osteuropa" name="Ukraine-Krieg (Jahr 5)" sev="critical"
          desc="Größter konventioneller Krieg in Europa seit 1945. Läuft im 5. Jahr. Dominiert europäische Sicherheits- und Energiepolitik." />
        <ConflictCard region="Afrika" name="Sudan & Sahel-Region" sev="high"
          desc="Hungersnot in Sudan. M23-Offensive: Goma gefallen. Drei Militärjuntas (Mali, Burkina Faso, Niger) — russische Wagner-Präsenz." />
        <ConflictCard region="DR Kongo" name="DRC-Bürgerkrieg" sev="high"
          desc="M23-Offensive hält an. Rohstoffreiche Region destabilisiert — kritische Mineralien für globale Lieferketten gefährdet." />
        <ConflictCard region="Südostasien" name="Myanmar-Bürgerkrieg (Jahr 5)" sev="high"
          desc="Anhaltender Bürgerkrieg im 5. Jahr. Humanitäre Katastrophe. Regionale Destabilisierung im ASEAN-Raum." />
        <ConflictCard region="Global" name="US–China Technologiekonflikt" sev="medium"
          desc="KI, Halbleiter und kritische Mineralien als Sicherheitsrisiken. China: Exportkontrollen auf Seltene Erden." />
      </div>

      {/* Trends */}
      <h2 className="font-display text-lg font-semibold text-t1 flex items-center gap-2.5 mb-4 after:flex-1 after:h-px after:bg-border">
        Politische Trends
      </h2>
      <div className="grid grid-cols-2 gap-3.5 mb-7 max-sm:grid-cols-1">
        {/* Tariff meter */}
        <div className="bg-surface border border-border rounded-lg p-5">
          <div className="text-[13px] font-semibold text-t2 mb-3.5">US-Zollsatz-Entwicklung 2022–2026</div>
          <div className="flex flex-col gap-2.5">
            {[
              { year: '2022', pct: 1.5,  fill: 12.7, color: 'var(--low)' },
              { year: '2023', pct: 2.8,  fill: 23.7, color: 'var(--low)' },
              { year: '2024', pct: 4.5,  fill: 38.1, color: 'var(--medium)' },
              { year: '2025', pct: 8.2,  fill: 69.5, color: 'var(--high)' },
              { year: '2026', pct: 11.8, fill: 100,  color: 'var(--critical)' },
            ].map(row => (
              <div key={row.year} className="grid gap-2.5 items-center" style={{ gridTemplateColumns: '42px 1fr 46px' }}>
                <span className="text-[12px] font-medium text-t2">{row.year}</span>
                <div className="h-2 bg-s2 rounded overflow-hidden">
                  <div className="h-full rounded" style={{ width: `${row.fill}%`, background: row.color }} />
                </div>
                <span className="text-[12px] font-semibold tabular-nums text-t1 text-right">{row.pct}%</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-t3 mt-3">54% der US-Importe betroffen · Höchster Stand seit den 1930er Jahren</p>
        </div>

        {/* Populism */}
        <div className="bg-surface border border-border rounded-lg p-5">
          <div className="text-[13px] font-semibold text-t2 mb-3.5">Rechtspopulismus Europa — Umfragewerte 2026</div>
          <div className="flex flex-col gap-1.5">
            {[
              { name: 'AfD',       country: 'Deutschland',    pct: 22, bar: 71, color: 'var(--critical)', trend: '↑', up: true },
              { name: 'RN',        country: 'Frankreich',     pct: 26, bar: 84, color: 'var(--high)',     trend: '↓', up: false },
              { name: 'Reform UK', country: 'Großbritannien', pct: 19, bar: 61, color: 'var(--high)',     trend: '↑', up: true },
              { name: 'Fidesz',    country: 'Ungarn',         pct: 31, bar: 100,color: 'var(--medium)',  trend: '→', up: null },
              { name: 'FPÖ',       country: 'Österreich',     pct: 28, bar: 90, color: 'var(--high)',     trend: '↑', up: true },
            ].map(p => (
              <div key={p.name} className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-md bg-s2">
                <span className="text-[12px] font-semibold text-t1 w-[68px]">{p.name}</span>
                <span className="text-[11px] text-t3 w-[72px]">{p.country}</span>
                <div className="flex-1 h-1.5 bg-border rounded overflow-hidden">
                  <div className="h-full rounded" style={{ width: `${p.bar}%`, background: p.color }} />
                </div>
                <span className="text-[12px] font-semibold tabular-nums text-t2 w-8 text-right">{p.pct}%</span>
                <span className={`text-[12px] font-bold w-4 text-right ${p.up === true ? 'text-critical' : p.up === false ? 'text-low' : 'text-t3'}`}>
                  {p.trend}
                </span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-t3 mt-3">Quellen: Pew Research, Chatham House, Al Jazeera · Sep 2026</p>
        </div>
      </div>

      {/* Conclusions */}
      <div className="bg-accent-bg border border-border border-l-4 border-l-accent rounded-lg p-6 mb-7">
        <h3 className="font-display text-[15px] font-semibold text-accent mb-3">
          Geopolitische Trendlinie: 5 Kernbefunde
        </h3>
        <ul className="space-y-2.5 list-none">
          {[
            ['Fragmentierung beschleunigt sich', '68% der Experten erwarten eine multipolare oder fragmentierte Weltordnung. Multilaterale Kooperationsmechanismen erodieren sichtbar.'],
            ['Handelskrieg als struktureller Bruch', 'US-Zölle von 1.5% auf 11.8% — Unternehmen priorisieren Resilienz über Effizienz. Strukturell erhöhte Kosten bleiben auch bei späteren Zollsenkungen.'],
            ['Energiemärkte unter Druck', 'Iran-Konflikt gefährdet Straße von Hormuz. Gold steigt als Safe-Haven. Inflationsrisiken könnten Leitzinspolitik beeinflussen.'],
            ['Europa politisch gespalten', 'Rechtspopulismus trotz Rückschlägen in Frankreich strukturell stark. AfD dominant in Ostdeutschland. FPÖ und Reform UK auf Wachstumskurs.'],
            ['KI als Risiko und Wachstumstreiber', 'KI-Desinformation unter den Top-5-Risiken. Gleichzeitig kompensierte KI-Güternachfrage 2025 die negativen Zolleffekte.'],
          ].map(([bold, rest]) => (
            <li key={bold} className="text-[13px] text-t1 pl-[18px] relative leading-relaxed before:content-['→'] before:absolute before:left-0 before:text-accent before:font-bold">
              <strong>{bold}:</strong> {rest}
            </li>
          ))}
        </ul>
      </div>

      {/* Sources */}
      <div className="border-t border-border pt-4 flex flex-wrap gap-1.5 items-center">
        <span className="text-[11px] font-bold uppercase tracking-widest text-t3 mr-1">Quellen</span>
        {[
          ['WEF Global Risks 2026',  'https://www.weforum.org/publications/global-risks-report-2026/'],
          ['ACLED Konfliktliste',    'https://acleddata.com/series/conflict-watchlist-2026'],
          ['PIIE Handelskrieg',      'https://www.piie.com/blogs/realtime-economics/2025/global-trade-war-update'],
          ['EY Geostrategic',        'https://www.ey.com/en_gl/insights/geostrategy/geostrategic-analysis'],
          ['Lazard Geopolitik',      'https://www.lazard.com/research-insights/top-geopolitical-trends-in-2026/'],
          ['Pew Research Populismus','https://www.pewresearch.org/global/2026/05/28/right-wing-populism-in-the-decade-since-brexit/'],
          ['KPMG Trade Outlook',     'https://kpmg.com/us/en/articles/2026/global-trade-outlook-2026.html'],
        ].map(([label, url]) => (
          <a key={label} href={url} target="_blank" rel="noopener noreferrer"
            className="text-[11px] text-accent border border-border rounded px-1.5 py-0.5 hover:bg-accent-bg transition-colors whitespace-nowrap">
            {label}
          </a>
        ))}
      </div>
    </main>
  )
}
